import { DIMENSION_IDS } from '@/constants'
import { defineChart } from '..'
import { tooltip, tooltipWithTitle } from '@/composables/useChart/theme'
import type { CartesianAxisOption } from 'echarts/types/src/coord/cartesian/AxisModel'

export default defineChart<
    [Value[], Dimension[], Dimension, boolean, Dimension['id']?]
>(
    (
        values,
        dimensions,
        { countryColor, comparisonCountryColor, id },
        condensed,
        selectedId
    ) => {
        /**
         * Use the params.
         */
        const { config } = useParams()

        /**
         * Use the countries.
         */
        const { getName } = useCountries()

        /**
         * Get the countries.
         */
        const countries = [config.country, config.compareCountry].filter(
            Boolean
        )

        /**
         * Show factors description.
         */
        const tooltipFormatter =
            id === DIMENSION_IDS.VULNERABILITY ? tooltipWithTitle : tooltip

        return {
            tooltip: {},
            legend: {
                show: !condensed,
                textStyle: {
                    color: 'black'
                }
            },
            grid: {
                left: '2.5%',
                right: '4%',
                top: '10%',
                bottom: '5%',
                containLabel: true
            },
            xAxis: {},
            yAxis: {},
            media: [
                {
                    query: {
                        minAspectRatio: 1
                    },
                    option: horizontalLayout(countries, condensed)
                },
                {
                    query: {
                        maxAspectRatio: 1
                    },
                    option: verticalLayout(countries, condensed)
                }
            ],
            export: {
                legend: {
                    top: '10%'
                },
                grid: {
                    top: '20%'
                },
                ...horizontalLayout(countries, false)
            },
            series: countries.map(iso => {
                const color =
                    iso === config.country
                        ? countryColor
                        : iso === config.compareCountry
                        ? comparisonCountryColor
                        : twColor('gray')

                return {
                    type: 'bar',
                    barGap: 0,
                    tooltip: {
                        formatter(params) {
                            const { dimension, country, value } =
                                params.data as {
                                    dimension: Dimension
                                    country: string
                                    value: number[]
                                }

                            return tooltipFormatter(
                                dimension.description || '-',
                                country,
                                value[0]
                            )
                        }
                    },
                    name: getName(iso),
                    color,
                    itemStyle: { color },
                    data: values
                        .filter(value => value.iso === iso)
                        .map(value => {
                            const dimension = dimensions.find(
                                dimension => dimension.id === value.id
                            )

                            return {
                                name: dimension?.name,
                                dimension,
                                id: value.id,
                                value: [value.value, dimension?.name ?? ''],
                                country: iso,
                                itemStyle: {
                                    opacity:
                                        !selectedId ||
                                        selectedId == dimension?.id
                                            ? 1
                                            : 0.5
                                }
                            }
                        })
                }
            })
        }
    }
)

/**
 * Horizontal layout options.
 */
function horizontalLayout(countries: Country['iso'][], condensed: boolean) {
    return {
        xAxis: categoryAxis('center', condensed),
        yAxis: valueAxis(),
        series: countries.map(() => ({
            type: 'bar',
            encode: {
                x: 1,
                y: 0
            }
        }))
    }
}

/**
 * Vertical layout options.
 */
function verticalLayout(countries: Country['iso'][], condensed: boolean) {
    return {
        xAxis: valueAxis(),
        yAxis: categoryAxis('left', condensed),
        series: countries.map(() => ({
            type: 'bar',
            encode: {
                x: 0,
                y: 1
            }
        }))
    }
}

/**
 * Create a category axis.
 */
function categoryAxis(align: 'left' | 'right' | 'center', condensed: boolean) {
    const width = 100

    return {
        type: 'category',
        axisTick: { show: false },
        axisLine: { show: false },
        offset: align === 'left' ? width : 0,
        triggerEvent: true,
        axisLabel: {
            show: !condensed,
            interval: 0,
            overflow: 'break',
            fontFamily: 'Rubik',
            align,
            width
        }
    } satisfies CartesianAxisOption
}

/**
 * Create a value axis.
 */
function valueAxis() {
    return {
        type: 'value',
        max: 100,
        axisTick: { show: false },
        axisLine: { show: false },
        axisLabel: {
            show: true
        },
        splitLine: {
            show: true
        }
    } satisfies CartesianAxisOption
}
